import React, { useMemo } from "react";
import { cn } from "../../../utils/cn";
import { isSameDisplayDateAsFortalezaToday } from "../scheduleGridModel";
import {
  buildScheduleGridMinWidthPx,
  buildScheduleGridTemplateColumns,
  SCHEDULE_GRID_GAP_PX,
} from "../scheduleGridTheme";
import type { AgendaConsultant, AgendaDayPayload } from "../agendaTypes";
import type { ScheduleGridSlot } from "../scheduleGridTypes";
import { isoDateToDisplayDate } from "../agendaDateFormat";
import { getFortalezaTodayIso } from "../agendaFiltersQuery";
import { useAgendaClock } from "../hooks/useAgendaClock";
import ConsultantRow from "./ConsultantRow";
import WeatherRow from "./WeatherRow";
import { AGENDA_FEATURE_FLAGS } from "../constants/agendaFeatureFlags";
import type { CityWeather } from "../agendaWeatherTypes";
import { resolveCityWeatherForLabel } from "../agendaWeatherCityLookup";

type CityGridProps = {
  dateIso: string;
  dayData: AgendaDayPayload;
  consultants: AgendaConsultant[];
  cityWeatherByName?: Map<string, CityWeather>;
  hourRainByCity?: Map<string, Map<string, number>>;
  weatherCities?: CityWeather[];
  weatherPreferredCity?: string | null;
  weatherCommercialRegionId?: number | null;
  weatherLoading?: boolean;
  weatherFailed?: boolean;
  highlightedScheduleIds?: Set<number>;
  canManageSchedules: boolean;
  initialStatus: number | undefined;
  dateSelected: string[];
  onSelectCell: (
    scheduleOption: { id: number; date: string; status: number },
    event: React.MouseEvent
  ) => void;
  onSelectCellMouseOver: (coord: string, event: React.MouseEvent) => void;
  onOpenVisitDetails: (coord: string) => void;
  detailPanelOpen?: boolean;
};

const CityGrid: React.FC<CityGridProps> = ({
  dateIso,
  dayData,
  consultants,
  cityWeatherByName,
  hourRainByCity,
  weatherCities = [],
  weatherPreferredCity,
  weatherCommercialRegionId,
  weatherLoading = false,
  weatherFailed = false,
  highlightedScheduleIds,
  canManageSchedules,
  initialStatus,
  dateSelected,
  onSelectCell,
  onSelectCellMouseOver,
  onOpenVisitDetails,
  detailPanelOpen = false,
}) => {
  const allowedCities = new Set(["Fortaleza", "São Paulo", "Recife"]);
  const currentHourKey = useAgendaClock();
  const displayDate = isoDateToDisplayDate(dateIso);
  const isTodayInFortaleza = isSameDisplayDateAsFortalezaToday(displayDate);
  const businessHours = dayData.business_hours;
  const schedules = dayData.schedules as ScheduleGridSlot[];

  const gridColumnOptions = useMemo(
    () => ({ preserveSlotWidth: detailPanelOpen }),
    [detailPanelOpen]
  );

  const gridTemplateColumns = useMemo(
    () => buildScheduleGridTemplateColumns(businessHours.length, gridColumnOptions),
    [businessHours.length, gridColumnOptions]
  );

  const gridMinWidthPx = useMemo(
    () => buildScheduleGridMinWidthPx(businessHours.length, gridColumnOptions),
    [businessHours.length, gridColumnOptions]
  );

  const citySummariesByName = useMemo(() => {
    const map = new Map<string, AgendaDayPayload["cities"][number]>();
    dayData.cities.forEach((city) => map.set(city.name, city));
    return map;
  }, [dayData.cities]);

  const visibleRegions = dayData.commercial_region.filter(
    (region) =>
      allowedCities.has(region.name) &&
      consultants.some((consultant) => consultant.city === region.name)
  );

  return (
    <div className="space-y-5">
      {visibleRegions.map((region, regionIndex) => {
        const cityConsultants = consultants.filter(
          (consultant) => consultant.city === region.name
        );
        const summary = citySummariesByName.get(region.name);
        const regionWeather = resolveCityWeatherForLabel(region.name, weatherCities, {
          preferredCity: weatherPreferredCity,
          commercialRegionId: weatherCommercialRegionId ?? undefined,
          allowSingleCityFallback: visibleRegions.length === 1,
        });

        return (
          <section
            key={region.relevance_order}
            id={`agenda-city-${region.name.replace(/\s+/g, "-").toLowerCase()}`}
            className={regionIndex > 0 ? "border-t border-[#EDF2F7] pt-5" : ""}
          >
            <header className="mb-3 flex min-w-0 items-center gap-2">
              <div className="flex shrink-0 items-baseline gap-x-2 gap-y-0.5">
                <h2 className="text-[13px] font-bold uppercase tracking-[0.1em] text-[#c4552b]">
                  {region.name}
                </h2>
                <span className="text-[13px] text-[#718096]">
                  {summary?.consultant_count ?? cityConsultants.length} consultores
                  · {summary?.occupied_slots ?? 0}/{summary?.total_slots ?? 0}{" "}
                  horários ocupados
                </span>
              </div>
              <span
                className="h-px min-w-4 flex-1 bg-[#E2E8F0]"
                aria-hidden
              />
            </header>

            <div className="min-w-0 overflow-x-auto [scrollbar-width:thin]">
              <div
                className="grid w-full"
                style={{
                  gridTemplateColumns,
                  gap: SCHEDULE_GRID_GAP_PX,
                  minWidth: gridMinWidthPx,
                }}
                role="grid"
              >
                <div
                  className="sticky left-0 z-[3] h-12 bg-[#F7FAFC]"
                  aria-hidden
                />
                {businessHours.map((hour) => {
                  const hourKey = hour.split(":")[0];
                  const currentKey = currentHourKey.split(":")[0];
                  const isNow = isTodayInFortaleza && hourKey === currentKey;
                  const label = hour.replace(":00", "h");

                  return (
                    <div
                      key={`head-${region.name}-${hour}`}
                      className={cn(
                        "relative flex h-12 items-center justify-center rounded-md px-0.5",
                        isNow && "bg-[#FFF5F0]"
                      )}
                    >
                      {isNow ? (
                        <span className="pointer-events-none inline-flex flex-col items-center justify-center gap-0.5 text-[#c4552b]">
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold leading-none">
                            <span
                              className="h-1.5 w-1.5 rounded-full bg-[#c4552b]"
                              aria-hidden
                            />
                            agora
                          </span>
                          <span className="text-sm font-bold leading-none">
                            {label}
                          </span>
                        </span>
                      ) : (
                        <span className="text-sm font-semibold leading-none text-[#4A5568]">
                          {label}
                        </span>
                      )}
                    </div>
                  );
                })}

                {AGENDA_FEATURE_FLAGS.weatherRow ? (
                  <WeatherRow
                    businessHours={businessHours}
                    cityWeather={
                      regionWeather ??
                      cityWeatherByName?.get(region.name) ??
                      null
                    }
                    loading={weatherLoading}
                    failed={weatherFailed}
                    isPastDate={dateIso < getFortalezaTodayIso()}
                  />
                ) : null}

                {cityConsultants.map((consultant) => (
                  <ConsultantRow
                    key={consultant.id}
                    consultant={consultant}
                    dateIso={dateIso}
                    businessHours={businessHours}
                    schedules={schedules}
                    currentHourKey={currentHourKey}
                    isTodayInFortaleza={isTodayInFortaleza}
                    dateSelected={dateSelected}
                    canManageSchedules={canManageSchedules}
                    initialStatus={initialStatus}
                    onSelectCell={onSelectCell}
                    onSelectCellMouseOver={onSelectCellMouseOver}
                    onOpenVisitDetails={onOpenVisitDetails}
                    highlightedScheduleIds={highlightedScheduleIds}
                    cityName={region.name}
                    hourRainByCity={hourRainByCity}
                  />
                ))}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default CityGrid;
